"use client";

import { useTranslations } from "next-intl";
import { domId } from "@/lib/dom-id";
import { Button } from "@/components/ui";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog";

interface UnsavedChangesModalProps {
  isOpen: boolean;
  onKeepEditing: () => void;
  onDiscard: () => void;
}

export default function UnsavedChangesModal({
  isOpen,
  onKeepEditing,
  onDiscard,
}: UnsavedChangesModalProps) {
  const t = useTranslations("UnsavedChangesModal");

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onKeepEditing()}>
      <DialogContent
        showClose={false}
        className="z-[70] max-w-md rounded-xl p-6"
        onPointerDownOutside={(event) => {
          event.preventDefault();
          onKeepEditing();
        }}
        onEscapeKeyDown={(event) => {
          event.preventDefault();
          onKeepEditing();
        }}
      >
        <DialogTitle className="sr-only">{t("title")}</DialogTitle>
        <DialogDescription className="sr-only">{t("message")}</DialogDescription>

        <div
          data-testid={domId("mdl", "unsaved-changes-modal")}
          className="w-full"
        >
          <div className="mb-4 flex items-start gap-3">
            <div className="mt-0.5 flex-shrink-0">
              <svg
                className="h-6 w-6 text-amber-500"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"
                />
              </svg>
            </div>
            <div className="flex-1">
              <h3 className="mb-1 text-lg font-semibold text-slate-900">
                {t("title")}
              </h3>
              <p className="text-sm text-slate-600">{t("message")}</p>
            </div>
          </div>

          <div className="flex justify-end gap-3">
            <Button
              data-testid={domId("btn", "unsaved-changes-discard")}
              onClick={onDiscard}
              variant="unstyled"
              className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700"
            >
              {t("actions.discard")}
            </Button>
            <Button
              data-testid={domId("btn", "unsaved-changes-keep-editing")}
              onClick={onKeepEditing}
              variant="unstyled"
              className="rounded-lg px-4 py-2 text-sm font-medium text-white"
              style={{
                background: "linear-gradient(to right, #713D98, #3E3D98)",
              }}
            >
              {t("actions.keep")}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
